import React, { useCallback } from "react";
import { 
  Users, 
  DollarSign, 
  Zap, 
  LifeBuoy, 
  CheckCircle, 
  LifeBuoy as TicketIcon,
  PenTool,
  Settings,
  Gift,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { useAdminDashboard } from "../../hooks/useAdminDashboard";
import { useNavigate } from "react-router-dom";

const AdminOverview = () => {
  const navigate = useNavigate();
  
  const {
    dashboardData,
    loading,
    error,
    refreshDashboard
  } = useAdminDashboard();


  // Handle quick action navigation
  const handleQuickAction = useCallback((action) => {
    if (action.url) {
      navigate(action.url);
    } else {
      // Handle different action types
      switch (action.action) {
        case 'navigate_to_support':
          navigate('/admin/support');
          break;
        case 'navigate_to_blog':
          navigate('/admin/blog/new');
          break;
        case 'navigate_to_pricing':
          navigate('/admin/pricing');
          break;
        case 'navigate_to_promo':
          navigate('/admin/promo-tokens');
          break;
        default:
          console.log('Action not implemented:', action.action);
      }
    }
  }, [navigate]);

  // Format numbers for display
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K';
      }
      return value.toLocaleString();
    }
    return value;
  };

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value === 'number') {
      return '$' + value.toLocaleString();
    }
    return value;
  };

  // Get icon component based on icon name
  const getIconComponent = (iconName) => {
    const iconMap = {
      'user-plus': Users,
      'dollar': DollarSign,
      'zap': Zap,
      'headset': LifeBuoy,
      'check-circle': CheckCircle,
      'ticket': TicketIcon,
      'pen': PenTool,
      'gift': Gift
    };
    return iconMap[iconName] || Users;
  };

  // Get color classes based on status/type
  const getColorClasses = (type, isIcon = false) => {
    const colorMap = {
      success: isIcon ? 'text-green-600' : 'text-green-600',
      warning: isIcon ? 'text-yellow-600' : 'text-yellow-600',
      error: isIcon ? 'text-red-600' : 'text-red-600',
      info: isIcon ? 'text-blue-600' : 'text-blue-600',
      positive: isIcon ? 'text-green-600' : 'text-green-600',
      neutral: isIcon ? 'text-gray-600' : 'text-gray-600'
    };
    return colorMap[type] || (isIcon ? 'text-gray-600' : 'text-gray-600');
  };

  const getBgClasses = (type) => {
    const bgMap = {
      success: 'bg-green-100',
      warning: 'bg-yellow-100',
      error: 'bg-red-100',
      info: 'bg-blue-100',
      positive: 'bg-green-100',
      neutral: 'bg-gray-100'
    };
    return bgMap[type] || 'bg-gray-100';
  };

  // Transform API data to component format
  const transformMetricsData = (metrics) => {
    if (!metrics) return [];
    
    return [
      {
        title: "Total Users",
        value: formatNumber(metrics.totalUsers?.value || 0),
        change: metrics.totalUsers?.changeLabel || "No data",
        changeType: metrics.totalUsers?.change > 0 ? "positive" : "neutral",
        icon: Users
      },
      {
        title: "Monthly Revenue",
        value: formatCurrency(metrics.monthlyRevenue?.value || 0),
        change: metrics.monthlyRevenue?.changeLabel || "No data",
        changeType: metrics.monthlyRevenue?.change > 0 ? "positive" : "neutral",
        icon: DollarSign
      },
      {
        title: "API Requests",
        value: formatNumber(metrics.apiRequests?.value || 0),
        change: metrics.apiRequests?.changeLabel || "No data",
        changeType: metrics.apiRequests?.change > 0 ? "positive" : "neutral",
        icon: Zap
      },
      {
        title: "Support Tickets",
        value: metrics.supportTickets?.value || 0,
        change: metrics.supportTickets?.pendingRequests ? 
          `${metrics.supportTickets.pendingRequests} pending requests` : "No pending requests",
        changeType: metrics.supportTickets?.pendingRequests > 0 ? "warning" : "neutral",
        icon: LifeBuoy
      },
      {
        title: "Active Purchases",
        value: formatNumber(metrics.activePurchases?.value || 0),
        change: metrics.activePurchases?.changeLabel || "No data",
        changeType: metrics.activePurchases?.change > 0 ? "positive" : "neutral",
        icon: CheckCircle
      },
      {
        title: "Average Revenue Per User",
        value: formatCurrency(metrics.avgRevenuePerUser?.value || 0),
        change: metrics.avgRevenuePerUser?.changeLabel || "No data",
        changeType: metrics.avgRevenuePerUser?.change > 0 ? "positive" : "neutral",
        icon: DollarSign
      }
    ];
  };

  const statsData = transformMetricsData(dashboardData.mainMetrics);
  const quickActions = dashboardData.quickActions?.actions || [];
  const recentActivities = dashboardData.recentActivity?.activities || [];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white rounded-lg p-6 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#075038]" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white rounded-lg p-6 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <h3 className="text-lg font-semibold text-gray-900">Failed to load dashboard</h3>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={refreshDashboard}
            className="flex items-center space-x-2 px-4 py-2 bg-[#075038] text-white rounded-lg hover:bg-[#064030] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white rounded-lg p-6 space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={refreshDashboard}
          className="p-2 text-gray-600 hover:text-[#075038] transition-colors"
          title="Refresh dashboard"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsData.slice(0, 4).map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
                <p className={`text-sm font-medium ${getColorClasses(stat.changeType)}`}>
                  {stat.change}
                </p>
              </div>
              <div className={`p-3 rounded-full ${getBgClasses(stat.changeType)}`}>
                <stat.icon className={`w-5 h-5 ${getColorClasses(stat.changeType, true)}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Second Row Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {statsData.slice(4).map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
                <p className={`text-sm font-medium ${getColorClasses(stat.changeType)}`}>
                  {stat.change}
                </p>
              </div>
              <div className={`p-3 rounded-full ${getBgClasses(stat.changeType)}`}>
                <stat.icon className={`w-5 h-5 ${getColorClasses(stat.changeType, true)}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        {console.log(quickActions)}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {quickActions.length > 0 ? (
              quickActions.map((action, index) => {
                const IconComponent = getIconComponent(action.icon);
                return (
                  <div 
                    key={action.id || index} 
                    onClick={() => handleQuickAction(action)}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group border-y"
                  >
                    <div className={`p-2 rounded-full ${getBgClasses(action.color)}`}>
                      <IconComponent className={`w-4 h-4 ${getColorClasses(action.color, true)}`} />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">{action.title}</span>
                      {action.description && (
                        <p className="text-xs text-gray-500 mt-1">{action.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {action.badge && (
                        <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                          {action.badge}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No quick actions available</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={activity.id || index} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                    activity.status === 'success' ? 'bg-green-500' :
                    activity.status === 'warning' ? 'bg-yellow-500' :
                    activity.status === 'error' ? 'bg-red-500' :
                    'bg-blue-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0">{activity.timeAgo}</span>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;